/**
 * BoardCanvas — shared Excalidraw canvas with autosave. Scene (elements +
 * appState) saves to Mongo; image files are offloaded to object storage as
 * one blob; thumbnail refresh is throttled. Used by the standalone Board
 * page and the in-editor board tab.
 */
import React, { useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Excalidraw, exportToBlob } from "@excalidraw/excalidraw";
import "@excalidraw/excalidraw/index.css";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SAVE_DEBOUNCE_MS = 900;
const THUMB_MIN_INTERVAL_MS = 12000;

function elementsSignature(elements, appState) {
  const sig = (elements || []).map((e) => `${e.id}:${e.version}`).join("|");
  return `${sig}#${appState?.viewBackgroundColor || ""}`;
}

function blobToDataURL(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

export function BoardCanvas({ board, onApi, onStatus }) {
  const navigate = useNavigate();
  const debounceRef = useRef(null);
  const dirtyRef = useRef(null);
  const lastSigRef = useRef(elementsSignature(board.data?.elements, board.data?.appState));
  const fileIdsRef = useRef(Object.keys(board.files || {}).sort().join(","));
  const lastThumbRef = useRef(0);

  const makeThumb = useCallback(async (elements, files, appState) => {
    try {
      const blob = await exportToBlob({
        elements,
        files: files || {},
        appState: { ...appState, exportBackground: true, viewBackgroundColor: appState?.viewBackgroundColor || "#ffffff" },
        mimeType: "image/jpeg",
        quality: 0.7,
        getDimensions: (w, h) => {
          const scale = Math.min(1, 480 / Math.max(w, h, 1));
          return { width: Math.round(w * scale), height: Math.round(h * scale), scale };
        },
      });
      return await blobToDataURL(blob);
    } catch { return null; }
  }, []);

  const save = useCallback(async (elements, appState, files) => {
    const payload = {
      data: { elements, appState: { viewBackgroundColor: appState?.viewBackgroundColor || "#ffffff" } },
    };
    const ids = Object.keys(files || {}).sort().join(",");
    if (ids !== fileIdsRef.current) { payload.files = files || {}; }
    if ((elements || []).length && Date.now() - lastThumbRef.current > THUMB_MIN_INTERVAL_MS) {
      const thumb = await makeThumb(elements, files, appState);
      if (thumb) { payload.thumbnail = thumb; lastThumbRef.current = Date.now(); }
    }
    onStatus && onStatus({ saving: true });
    try {
      await api.put(`/boards/${board.id}`, payload);
      if (payload.files) fileIdsRef.current = ids;
      onStatus && onStatus({ saving: false, savedAt: Date.now() });
    } catch (e) {
      onStatus && onStatus({ saving: false });
      if (e.response?.status === 402) { navigate("/locked"); return; }
      toast.error("Couldn't save the board. Retrying on your next edit.");
    }
  }, [board.id, navigate, makeThumb, onStatus]);

  const onChange = useCallback((elements, appState, files) => {
    const sig = elementsSignature(elements, appState);
    if (sig === lastSigRef.current) return; // ignore pure selection/viewport changes
    lastSigRef.current = sig;
    dirtyRef.current = { elements, appState, files };
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const d = dirtyRef.current;
      if (d) save(d.elements, d.appState, d.files);
    }, SAVE_DEBOUNCE_MS);
  }, [save]);

  return (
    <div className="h-full w-full" data-testid="board-canvas">
      <Excalidraw
        excalidrawAPI={(a) => { onApi && onApi(a); }}
        initialData={{
          elements: board.data?.elements || [],
          files: board.files || {},
          appState: { viewBackgroundColor: board.data?.appState?.viewBackgroundColor || "#ffffff" },
          scrollToContent: true,
        }}
        onChange={onChange}
        theme="light"
      />
    </div>
  );
}
